/*
 * A11y runtime shims for third-party app blocks and CMS surfaces we don't
 * fully control via Liquid templates.
 *
 * Phase 1 (US-7794):
 *  - Firework <fw-embed-feed>: tabindex=0, role=region, aria-label.
 *  - Firework <fw-storyblock>/<fw-story>: tabindex=0, aria-label, role=region.
 *  - Okendo media carousel buttons (.oke-mediaCarousel-prev/next): aria-label.
 *
 * Phase 2 (US-7798):
 *  - AC22 Closeby store-locator iframe: title.
 *  - AC23 fw-storyblock without role attribute: role=region.
 *  - AC24 Theme/wrapper scrollable regions without keyboard access: tabindex=0.
 *  - AC26 Heading hierarchy: demote extra <h1>s on product pages.
 *  - AC27 Recharge customer portal: sanitise React-Aria IDs containing "/".
 *  - AC28 Recharge customer portal: inject contrast-safe error CSS.
 *  - AC31 Backstop: any <img> without an alt attribute is treated decorative.
 *  - AC18 Backstop: empty <a> with no accessible name and no alt'd img.
 *
 * The :not([aria-label]) guard means we defer to a vendor the moment they
 * ship a fix upstream — zero conflict when that happens.
 */
(function () {
  'use strict';

  var OKENDO_LABELS = {
    'oke-mediaCarousel-prev': 'Previous review media',
    'oke-mediaCarousel-next': 'Next review media',
    'oke-w-mediaCarousel-prev': 'Previous review media',
    'oke-w-mediaCarousel-next': 'Next review media',
    'oke-carousel-prev': 'Previous review media',
    'oke-carousel-next': 'Next review media'
  };

  function setAttrIfMissing(el, name, value) {
    if (!el.hasAttribute(name) || el.getAttribute(name) === '' || el.getAttribute(name) === 'null') {
      el.setAttribute(name, value);
    }
  }

  function patchFirework(root) {
    root.querySelectorAll('fw-embed-feed').forEach(function (el) {
      setAttrIfMissing(el, 'tabindex', '0');
      setAttrIfMissing(el, 'role', 'region');
      setAttrIfMissing(el, 'aria-label', 'Shoppable video feed');
    });
    root.querySelectorAll('fw-storyblock, fw-story').forEach(function (el) {
      setAttrIfMissing(el, 'tabindex', '0');
      setAttrIfMissing(el, 'role', 'region');
      setAttrIfMissing(el, 'aria-label', 'Shoppable video');
    });
  }

  function patchOkendoIn(root) {
    Object.keys(OKENDO_LABELS).forEach(function (cls) {
      root.querySelectorAll('.' + cls).forEach(function (el) {
        var current = el.getAttribute('aria-label');
        if (!current || current === 'null') {
          el.setAttribute('aria-label', OKENDO_LABELS[cls]);
        }
      });
    });
    root.querySelectorAll('button[class*="oke-"]').forEach(function (btn) {
      var hasName = btn.hasAttribute('aria-label')
        || btn.hasAttribute('aria-labelledby')
        || (btn.textContent && btn.textContent.trim().length > 0);
      if (hasName) return;
      var cls = btn.className || '';
      if (/prev/i.test(cls)) {
        btn.setAttribute('aria-label', 'Previous review media');
      } else if (/next/i.test(cls)) {
        btn.setAttribute('aria-label', 'Next review media');
      } else {
        btn.setAttribute('aria-label', 'Reviews control');
      }
    });
  }

  // AC22: Closeby iframe ships without a title — the embed is built at runtime.
  function patchCloseby(root) {
    var hosts = root.querySelectorAll('[class*="closeby-embed-"]');
    hosts.forEach(function (host) {
      host.querySelectorAll('iframe').forEach(function (iframe) {
        if (!iframe.title && !iframe.getAttribute('aria-label')) {
          iframe.setAttribute('title', 'Find a BURST retailer near you');
        }
      });
    });
  }

  // AC27: React-Aria emits ids containing "/" and ":" (e.g.
  // "react-aria...-tabpanel-/overview", ":r3:-tab"). Both are invalid in an
  // HTML id reference under the ARIA IDREF rules that axe-valid-attr-value
  // enforces. Replace both with "-".
  function sanitiseIdRefs(s) { return (s || '').replace(/[\/:]/g, '-'); }
  function patchRechargeIds(root) {
    root.querySelectorAll('[id]').forEach(function (el) {
      var fixed = sanitiseIdRefs(el.id);
      if (fixed !== el.id) el.id = fixed;
    });
    ['aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns'].forEach(function (attr) {
      root.querySelectorAll('[' + attr + ']').forEach(function (el) {
        var val = el.getAttribute(attr) || '';
        var fixed = sanitiseIdRefs(val);
        if (fixed !== val) el.setAttribute(attr, fixed);
      });
    });

    // AC27 follow-up: Recharge's React tabs declare aria-controls pointing to
    // tabpanel ids that the app never renders (URL-driven router, no local
    // tabpanel siblings). After ID sanitisation, drop any aria-controls whose
    // target still does not resolve — keeps axe aria-valid-attr-value clean and
    // is semantically correct (the tab isn't controlling a local landmark).
    if (location.pathname.indexOf('/tools/recurring/') === 0) {
      root.querySelectorAll('[aria-controls]').forEach(function (el) {
        var id = el.getAttribute('aria-controls');
        if (!id) return;
        if (!document.getElementById(id)) el.removeAttribute('aria-controls');
      });
    }
  }

  // AC28: Recharge portal contrast overrides. Scoped to /tools/recurring/.
  //  - error-message: #ee3d11 on #e1d8ea -> 4.5+:1 with #b21a00
  //  - line-price-compare-at-price (strikethrough): #a1a1a1 on #fff (2.58:1)
  //  - inactive subscriptions sub-tab label: same #a1a1a1 issue
  //   Both bumped to #767676 (4.54:1).
  var rechargeStyleInjected = false;
  function injectRechargeStyles() {
    if (rechargeStyleInjected) return;
    if (location.pathname.indexOf('/tools/recurring/') !== 0) return;
    rechargeStyleInjected = true;
    var s = document.createElement('style');
    s.setAttribute('data-a11y-patch', 'recharge-contrast');
    s.textContent = [
      '[data-testid="error-message"] { color: #b21a00 !important; }',
      '[data-testid="line-price-compare-at-price"],',
      '[id*="tab-inactive"] { color: #767676 !important; }'
    ].join('\n');
    (document.head || document.documentElement).appendChild(s);
  }

  // AC26 (Recharge portal): the hosted React app renders no <h1>. Inject a
  // visually-hidden h1 derived from the URL segment so the page has exactly
  // one h1 (and not zero). Scoped to /tools/recurring/portal/.
  var rechargeHeadingInjected = false;
  function injectRechargeHeading() {
    if (rechargeHeadingInjected) return;
    if (location.pathname.indexOf('/tools/recurring/') !== 0) return;
    if (document.querySelector('h1')) { rechargeHeadingInjected = true; return; }
    if (!document.body) return;
    var path = location.pathname.toLowerCase();
    var label = 'My Subscriptions';
    if (path.indexOf('/portal/') !== -1) {
      if (path.indexOf('/overview') !== -1) label = 'My Subscriptions';
      else if (path.indexOf('/subscriptions') !== -1) label = 'My Subscriptions';
      else if (path.indexOf('/orders') !== -1) label = 'My Orders';
      else if (path.indexOf('/payment') !== -1) label = 'Payment Methods';
      else if (path.indexOf('/account') !== -1) label = 'My Account';
      else if (path.indexOf('/addresses') !== -1) label = 'My Addresses';
    } else if (path.indexOf('/login') !== -1) {
      label = 'Subscription Portal Login';
    }
    var h1 = document.createElement('h1');
    h1.className = 'visually-hidden';
    h1.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    h1.textContent = label;
    document.body.insertBefore(h1, document.body.firstChild);
    rechargeHeadingInjected = true;
  }

  // AC37: keen-slider marks off-screen slides aria-hidden="true". Its built-in
  // ADA pass clears tabindex on focusable descendants, but only after `mounted`
  // — axe sometimes scans before that, and other vendors (Yotpo, custom
  // carousels) ship aria-hidden=true with focusables inside unconditionally.
  // Belt-and-braces: any aria-hidden=true ancestor implies tabindex=-1 on
  // every focusable descendant. Storing the original value under
  // data-a11y-orig-tabindex so we can restore it when the slide becomes
  // visible again (handled by the slider's own pass on slideChanged).
  var FOCUSABLE_INSIDE_HIDDEN = 'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"], audio[controls], video[controls], iframe';

  function patchAriaHiddenFocus(root) {
    if (!root.querySelectorAll) return;
    root.querySelectorAll('[aria-hidden="true"]').forEach(function (host) {
      host.querySelectorAll(FOCUSABLE_INSIDE_HIDDEN).forEach(function (el) {
        if (el.closest('[aria-hidden="false"]') && el.closest('[aria-hidden="false"]') !== host) return;
        var current = el.getAttribute('tabindex');
        if (current === '-1') return;
        if (current !== null && !el.hasAttribute('data-a11y-orig-tabindex')) {
          el.setAttribute('data-a11y-orig-tabindex', current);
        } else if (current === null && !el.hasAttribute('data-a11y-orig-tabindex')) {
          el.setAttribute('data-a11y-orig-tabindex', '');
        }
        el.setAttribute('tabindex', '-1');
      });
    });
    // Restore tabindex when a previously hidden ancestor becomes visible.
    root.querySelectorAll('[data-a11y-orig-tabindex]').forEach(function (el) {
      var hiddenAncestor = el.closest('[aria-hidden="true"]');
      if (hiddenAncestor) return;
      var orig = el.getAttribute('data-a11y-orig-tabindex');
      if (orig === '') el.removeAttribute('tabindex');
      else el.setAttribute('tabindex', orig);
      el.removeAttribute('data-a11y-orig-tabindex');
    });
  }

  // AC24: Make scrollable regions keyboard-focusable. We skip Firework's
  // internal scrollers (a documented vendor-controlled exception) and skip
  // anything already focusable / inside a known dialog/scroll container we
  // already handle.
  var SCROLL_SKIP_SELECTOR = [
    'fw-embed-feed *', 'fw-storyblock *', 'fw-story *',
    '[class*="fwn-"]',
    'input', 'textarea', 'select',
    'button', 'a[href]',
    '[tabindex]',
    '[contenteditable="true"]'
  ].join(',');

  function isScrollable(el) {
    if (!el || !el.style) return false;
    if (el.scrollHeight - el.clientHeight < 4 && el.scrollWidth - el.clientWidth < 4) return false;
    var cs = window.getComputedStyle(el);
    var oy = cs.overflowY;
    var ox = cs.overflowX;
    return (oy === 'auto' || oy === 'scroll') || (ox === 'auto' || ox === 'scroll');
  }

  function patchScrollableRegions(root) {
    if (!root.querySelectorAll) return;
    var candidates = root.querySelectorAll('div, section, article, aside, main');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.hasAttribute('tabindex')) continue;
      if (el.matches && el.matches(SCROLL_SKIP_SELECTOR)) continue;
      if (el.closest && el.closest('fw-embed-feed, fw-storyblock, fw-story, [class*="fwn-"]')) continue;
      if (!isScrollable(el)) continue;
      el.setAttribute('tabindex', '0');
      // Add a role + label only if it isn't inside a labelled landmark already.
      if (!el.getAttribute('role') && !el.closest('[role="region"], section[aria-label], section[aria-labelledby]')) {
        el.setAttribute('role', 'region');
        if (!el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby')) {
          el.setAttribute('aria-label', 'Scrollable content');
        }
      }
    }
  }

  // AC26: Heading hierarchy on PDPs. Force exactly one <h1>.
  // - Demote extra <h1>s to <h2> (keep classes/attrs).
  // - If zero <h1>s on a PDP, promote the first prominent <h2> (typically
  //   product title) to <h1>.
  function isProductPage() {
    return /^\/products\//.test(location.pathname);
  }

  // AC39: also enforce single-H1 on search results, 404, and account auth
  // pages where the Reg #4 audit flagged 2+ H1s.
  function pageWantsSingleH1() {
    if (isProductPage()) return true;
    var p = location.pathname;
    if (/^\/search(\/|$|\?)/.test(p)) return true;
    if (/^\/account\/(login|register)/.test(p)) return true;
    if (document.body && document.body.classList && document.body.classList.contains('template-404')) return true;
    return false;
  }

  function changeHeadingTag(oldEl, newTagName) {
    if (!oldEl || !oldEl.parentNode) return null;
    var newEl = document.createElement(newTagName);
    var attrs = oldEl.attributes;
    var names = [];
    for (var i = 0; i < attrs.length; i++) names.push(attrs[i].name);
    for (var j = 0; j < names.length; j++) {
      var nm = names[j];
      var val = oldEl.getAttribute(nm);
      if (val !== null) newEl.setAttribute(nm, val);
    }
    while (oldEl.firstChild) newEl.appendChild(oldEl.firstChild);
    oldEl.parentNode.replaceChild(newEl, oldEl);
    return newEl;
  }

  // PRODUCT_TITLE_SELECTORS: ordered list of ancestor selectors that mark the
  // "primary" product title H1. First match wins. Anything outside these is
  // demoted on a PDP. The list is generous to cover the various landing-page
  // templates that wrap the title in different containers.
  var PRODUCT_TITLE_SELECTORS = [
    '.main-product-wrap',
    '.main-product-info',
    '[id^="shopify-section-product_main"]',
    '[id*="product_main"]',
    '[class*="product_main"]',
    '[class*="product-main"]',
    '[data-section-type="product_main"]'
  ].join(',');

  function patchHeadingHierarchy() {
    if (!pageWantsSingleH1()) return;
    var h1s = Array.prototype.slice.call(document.querySelectorAll('h1'));
    if (h1s.length > 1) {
      var primary = null;
      for (var i = 0; i < h1s.length; i++) {
        if (h1s[i].closest && h1s[i].closest(PRODUCT_TITLE_SELECTORS)) {
          primary = h1s[i];
          break;
        }
      }
      if (!primary) primary = h1s[0];
      for (var k = 0; k < h1s.length; k++) {
        if (h1s[k] !== primary) {
          try { changeHeadingTag(h1s[k], 'h2'); } catch (e) {}
        }
      }
    } else if (h1s.length === 0) {
      var candidate = document.querySelector('.main-product-wrap h2, .main-product-info h2, [class*="product_main"] h2');
      if (candidate) {
        try { changeHeadingTag(candidate, 'h1'); } catch (e) {}
      }
    }
  }

  // AC16: header mega-menu toggles emit aria-controls="submenu-{handle}-header"
  // (from main_menu_item.liquid). For regular dropdowns the matching panel is
  // rendered alongside the toggle. For mega-menu triggers, the panel is
  // injected at runtime by mega_menu_content.liquid without an id. Sync the
  // injected panel's id to the trigger's aria-controls so the IDREF resolves.
  function patchMegaMenuPanels(root) {
    if (!root.querySelectorAll) return;
    root.querySelectorAll('.header-main-menu [aria-controls]').forEach(function (trigger) {
      var ctrlId = trigger.getAttribute('aria-controls');
      if (!ctrlId) return;
      if (document.getElementById(ctrlId)) return;
      var item = trigger.closest('.has-dropdown') || trigger.parentNode;
      if (!item) return;
      var children = item.children;
      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        if (child.classList && child.classList.contains('submenu')) {
          if (!child.id) child.id = ctrlId;
          return;
        }
      }
    });
  }

  // AC31 backstop: any <img> without an alt attribute is treated as
  // decorative. Templates that emit alt explicitly remain untouched.
  function patchMissingAlts(root) {
    root.querySelectorAll('img:not([alt])').forEach(function (img) {
      img.setAttribute('alt', '');
    });
  }

  // AC18 backstop: empty <a> with no accessible name and no labelled img
  // child — hide from a11y tree (avoid axe link-name violation). Keeps the
  // link clickable for sighted users while signaling decorative.
  function patchEmptyLinks(root) {
    root.querySelectorAll('a[href]').forEach(function (a) {
      if (a.hasAttribute('aria-label') || a.hasAttribute('aria-labelledby')) return;
      var text = (a.textContent || '').trim();
      if (text.length > 0) return;
      var labelledImg = a.querySelector('img[alt]:not([alt=""])');
      if (labelledImg) return;
      // Derive a label from href as a safe fallback.
      var href = a.getAttribute('href') || '';
      var slug = href.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() || '';
      var label = slug.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
      if (label) {
        a.setAttribute('aria-label', label.charAt(0).toUpperCase() + label.slice(1));
      } else {
        a.setAttribute('aria-hidden', 'true');
        a.setAttribute('tabindex', '-1');
      }
    });
  }

  function walkShadowRoots(root, callback) {
    callback(root);
    var all = root.querySelectorAll ? root.querySelectorAll('*') : [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) walkShadowRoots(all[i].shadowRoot, callback);
    }
  }

  function safe(fn) { try { fn(); } catch (e) { /* never block render */ } }

  function runAllPatches() {
    safe(injectRechargeStyles);
    safe(injectRechargeHeading);
    safe(patchHeadingHierarchy);
    try {
      walkShadowRoots(document, function (root) {
        safe(function () { patchFirework(root); });
        safe(function () { patchOkendoIn(root); });
        safe(function () { patchCloseby(root); });
        safe(function () { patchRechargeIds(root); });
        safe(function () { patchMegaMenuPanels(root); });
        safe(function () { patchMissingAlts(root); });
        safe(function () { patchEmptyLinks(root); });
        safe(function () { patchScrollableRegions(root); });
        safe(function () { patchAriaHiddenFocus(root); });
      });
    } catch (e) { /* never block render */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAllPatches);
  } else {
    runAllPatches();
  }

  var pending = false;
  var observer = new MutationObserver(function () {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      runAllPatches();
    });
  });
  function startObserving() {
    if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  }
  if (document.body) startObserving();
  else document.addEventListener('DOMContentLoaded', startObserving);

  var pollCount = 0;
  var pollMax = 30;
  var pollId = setInterval(function () {
    pollCount++;
    runAllPatches();
    if (pollCount >= pollMax) clearInterval(pollId);
  }, 500);

  ['okendo:reviews-loaded', 'okendo:loaded', 'okeReviewsWidget:rendered'].forEach(function (evt) {
    window.addEventListener(evt, runAllPatches);
    document.addEventListener(evt, runAllPatches);
  });

  window.__a11yPatchNow = runAllPatches;
})();
